// A date range in one field (finance-real, the user 2026-10-22: "I choose a start date and in the same date picker I can
// automatically choose monthly so it sets the end date"). The field reads the range; it opens a calendar (react-day-picker,
// as ScheduleCalendar uses) where the first click is the start and the second the end. Shortcuts sit inside the popover,
// under the calendar, and fill the end from the start (DD-061: a shortcut lives inside the field it fills); the end can
// still be any day picked by hand. `single` picks a start alone. Days are ISO strings, "" for none. The popover never
// portals: inside a drawer a menu outside the dialog cannot be clicked (DD-051), so it opens up when the drawer's body has
// no room below, and ends at the field's right edge when the body (or the window) has no room for it to the right, as
// DropdownMenu picks its direction (the S6 walk: in a drawer's right-hand column it ran past the panel). The field and its hints write days and ranges as every finance period reads (periodText): "8 Sep 2026",
// "October 2026", "21 Oct-20 Nov 2026". `single` is the one day picker too (DayField).
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { CalendarCheckIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { inputClassName } from "@/components/ui/FormField";
import { dateLabel } from "@/domains/Finance/utils/format";
import { periodText } from "@/domains/Finance/utils/names";
import { cn } from "@/utils/cn";

export interface IsoRange {
  /** The first day, "" for none. */
  from: string;
  /** The last day, "" for none: a range still being picked, or one day. */
  to: string;
}

export interface RangeShortcut {
  label: string;
  /** The last day it gives a range starting on `from`. */
  endFrom: (from: string) => string;
}

interface Props {
  value: IsoRange;
  onChange: (value: IsoRange) => void;
  /** Names the field. */
  ariaLabel: string;
  /** A start day alone: no end, no shortcuts. */
  single?: boolean;
  shortcuts?: RangeShortcut[];
  /** Offer Clear: no day is a valid answer. */
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** The last day it offers, ISO; days after it cannot be picked (a payment's day is never after today). */
  max?: string;
  /** Marks the field aria-required, beside the star in its label. */
  required?: boolean;
}

const toDate = (iso: string): Date | undefined => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const toIso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** The calendar's size and colours, set on its root, where react-day-picker's stylesheet declares them: the form's blue,
 * and days that fit a drawer's width. */
const CALENDAR_STYLE = {
  "--rdp-accent-color": "#2563eb",
  "--rdp-accent-background-color": "#eff6ff",
  "--rdp-day-height": "36px",
  "--rdp-day-width": "36px",
  "--rdp-day_button-height": "34px",
  "--rdp-day_button-width": "34px",
  "--rdp-nav-height": "2.25rem",
  fontSize: "13px",
} as CSSProperties;

/** react-day-picker's stylesheet sets selected days in a large bold font, so a range's days outgrow the rest of the month:
 * they keep the calendar's size. */
const SELECTED_STYLE = { selected: { fontSize: "inherit", fontWeight: 600 } };


/** The room the popover wants below its field before it opens up instead. */
const POPOVER_ROOM = 360;

/** Up when the drawer's body (or the window) has less room below the field than above it and too little for the popover. */
function opensUp(field: HTMLElement): boolean {
  const rect = field.getBoundingClientRect();
  const body = field.closest("[data-drawer-body]")?.getBoundingClientRect();
  const below = Math.min(window.innerHeight, body?.bottom ?? window.innerHeight) - rect.bottom;
  const above = rect.top - Math.max(0, body?.top ?? 0);
  return below < POPOVER_ROOM && above > below;
}

/** The popover's width: seven 36px days, its padding and border (CALENDAR_STYLE). */
const POPOVER_WIDTH = 272;

/** Anchored to the field's right edge when the drawer's body (or the window) has less room right of the field's left edge
 * than the popover needs, and more room left of its right edge. */
function endsRight(field: HTMLElement): boolean {
  const rect = field.getBoundingClientRect();
  const body = field.closest("[data-drawer-body]")?.getBoundingClientRect();
  const right = Math.min(window.innerWidth, body?.right ?? window.innerWidth) - rect.left;
  const left = rect.right - Math.max(0, body?.left ?? 0);
  return right < POPOVER_WIDTH && left > right;
}

export function DateRangePicker({ value, onChange, ariaLabel, single = false, shortcuts = [], clearable = false, placeholder = "Pick the days", disabled = false, max, required = false }: Props) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [end, setEnd] = useState(false);
  // The next click's day: the start, or the end once a start is picked in this opening.
  const [picking, setPicking] = useState<"from" | "to">("from");
  const ref = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    // Escape closes the calendar alone: on window in the capture phase, ahead of the drawer's (Radix's) Escape on the
    // document, and stopped there, so the drawer and its form stay open.
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    window.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("mousedown", outside);
      window.removeEventListener("keydown", escape, true);
    };
  }, [open]);

  const toggle = () => {
    if (!open && ref.current) {
      setUp(opensUp(ref.current));
      setEnd(endsRight(ref.current));
      setPicking("from");
    }
    setOpen(!open);
  };

  const pick = (day: Date) => {
    const iso = toIso(day);
    if (single) {
      onChange({ from: iso, to: "" });
      setOpen(false);
      return;
    }
    if (picking === "from" || !value.from) {
      onChange({ from: iso, to: "" });
      setPicking("to");
      return;
    }
    // An end before the start makes it the start: the range reads forwards.
    onChange(iso < value.from ? { from: iso, to: value.from } : { from: value.from, to: iso });
    setOpen(false);
  };

  const text = !value.from ? "" : single ? dateLabel(value.from) : periodText(value.from, value.to);
  const from = toDate(value.from);
  const to = toDate(value.to);
  const last = toDate(max ?? "");
  const after = last ? { after: last } : undefined;

  return (
    <div ref={ref} className="relative">
      {/* A combobox opening a dialog (the ARIA date picker pattern), so it can say it is required: a button cannot. */}
      <button
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={toggle}
        className={cn(inputClassName, "flex items-center gap-2 text-left", disabled && "cursor-not-allowed opacity-60")}
      >
        <CalendarCheckIcon className="w-4 h-4 shrink-0 text-gray-400" />
        <span className={cn("truncate", !text && "text-gray-400")}>{text || placeholder}</span>
        {!single && value.from && !value.to && picking === "to" && open && <span className="ml-auto text-xs text-gray-400">pick the end</span>}
      </button>
      {open && (
        // Marked as an open menu, so an inline form's Escape (EscapeScope) leaves the key to the calendar, which closes alone.
        // Inside a FormField's <label>, a click on the caption, the weekdays or between the days would be forwarded to the
        // field, which toggles it shut: the popover cancels that, and its own buttons still work.
        <div
          id={popoverId}
          role="dialog"
          aria-label={ariaLabel}
          data-dropdown-menu=""
          onClick={(e) => e.preventDefault()}
          className={cn("absolute z-50 rounded-lg border border-gray-200 bg-white p-2 shadow-lg", end ? "right-0" : "left-0", up ? "bottom-full mb-1" : "top-full mt-1")}
        >
          {single ? (
            <DayPicker mode="single" selected={from} defaultMonth={from} disabled={after} onSelect={(_, day) => pick(day)} style={CALENDAR_STYLE} modifiersStyles={SELECTED_STYLE} />
          ) : (
            <DayPicker mode="range" selected={from ? { from, to } : undefined} defaultMonth={from} disabled={after} onSelect={(_, day) => pick(day)} style={CALENDAR_STYLE} modifiersStyles={SELECTED_STYLE} />
          )}
          {(shortcuts.length > 0 || clearable) && !single && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-1 pt-2">
              {shortcuts.map((s) => (
                <Button
                  key={s.label}
                  size="sm"
                  variant="secondary"
                  disabled={!value.from}
                  title={value.from ? periodText(value.from, s.endFrom(value.from)) : "Pick the start first"}
                  onClick={() => {
                    onChange({ from: value.from, to: s.endFrom(value.from) });
                    setOpen(false);
                  }}
                >
                  {s.label}
                </Button>
              ))}
              {!value.from && shortcuts.length > 0 && <span className="text-xs text-gray-500">Pick the start first</span>}
              {clearable && value.from && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => {
                    onChange({ from: "", to: "" });
                    setOpen(false);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
